import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppButton from '../components/WhatsAppButton';

const NewLeads = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canViewPage = hasPermission('new_leads_page', 'read');

  const [applicants, setApplicants] = useState([]);
  const [failedApplicants, setFailedApplicants] = useState([]);
  const [metrics, setMetrics] = useState({
    last12Hours: 0,
    last72Hours: 0,
    last7DaysTotal: 0,
    last7DaysAvgPerDay: 0
  });
  const [markingSentIds, setMarkingSentIds] = useState([]);
  const [markingFailedIds, setMarkingFailedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedPhoneId, setCopiedPhoneId] = useState(null);

  useEffect(() => {
    if (!canViewPage) {
      setLoading(false);
      return;
    }

    fetchNewLeads();
  }, [canViewPage]);

  const fetchNewLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/new-leads');
      setApplicants(response.data.applicants || []);
      setFailedApplicants(response.data.failedApplicants || []);
      setMetrics(response.data.metrics || {
        last12Hours: 0,
        last72Hours: 0,
        last7DaysTotal: 0,
        last7DaysAvgPerDay: 0
      });
    } catch (error) {
      console.error('Error fetching new leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkMsgSent = async (applicantId) => {
    if (markingSentIds.includes(applicantId)) return;
    setMarkingSentIds(prev => [...prev, applicantId]);
    try {
      const response = await axios.post(`/api/new-leads/${applicantId}/msg-sent`);
      setApplicants(prev => prev.filter(applicant => applicant.id !== applicantId));
      setFailedApplicants(prev => prev.filter(applicant => applicant.id !== applicantId));
      if (response.data.metrics) {
        setMetrics(response.data.metrics);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error marking message sent');
    } finally {
      setMarkingSentIds(prev => prev.filter(id => id !== applicantId));
    }
  };

  const handleMarkWaFailed = async (applicantId) => {
    if (markingFailedIds.includes(applicantId)) return;
    setMarkingFailedIds(prev => [...prev, applicantId]);
    try {
      await axios.post(`/api/new-leads/${applicantId}/wa-failed`);
      const movedApplicant = applicants.find(applicant => applicant.id === applicantId);
      setApplicants(prev => prev.filter(applicant => applicant.id !== applicantId));
      if (movedApplicant) {
        setFailedApplicants(prev => [
          { ...movedApplicant, status: 'unable_to_send_message', lastActivityAt: new Date().toISOString() },
          ...prev.filter(applicant => applicant.id !== applicantId)
        ]);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error marking unable to send message');
    } finally {
      setMarkingFailedIds(prev => prev.filter(id => id !== applicantId));
    }
  };

  const handleCopyPhone = (countryCode, phone, uniqueId) => {
    const fullNumber = `${countryCode}${phone}`.replace(/\s+/g, '');
    navigator.clipboard.writeText(fullNumber);
    setCopiedPhoneId(uniqueId);
    setTimeout(() => setCopiedPhoneId(null), 1500);
  };

  if (!canViewPage) {
    return (
      <Layout>
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6 text-red-600">
            Access denied. You do not have permission to view this page.
          </div>
        </div>
      </Layout>
    );
  }

  const avgLast3Days = Math.round((metrics.last72Hours / 3) * 10) / 10;
  const avgLast7Days = Math.round((metrics.last7DaysTotal / 7) * 10) / 10;

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">New Leads</h1>
          <p className="text-sm text-gray-500 mt-1">Most recent 30 applicants with status: new lead</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Today)</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{metrics.last12Hours}</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Last 3 Days)</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{metrics.last72Hours}</div>
            <div className="text-xs text-gray-500 mt-1">{avgLast3Days} avg/day</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Last 7 Days)</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{metrics.last7DaysTotal}</div>
            <div className="text-xs text-gray-500 mt-1">{avgLast7Days} avg/day</div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <div className="text-gray-500">Loading new leads...</div>
          </div>
        ) : applicants.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <div className="text-gray-500">No new leads found.</div>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Msg Sent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Couldn't Send WA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead Source
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applicants.map((applicant) => (
                  <tr key={applicant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/applicants/${applicant.id}`)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline text-left block truncate max-w-[200px]"
                        title={applicant.name}
                      >
                        {applicant.name}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        {applicant.countryCode || '+91'} {applicant.phone}
                        <button
                          onClick={() => handleCopyPhone(applicant.countryCode || '+91', applicant.phone, `${applicant.id}-phone1`)}
                          className="ml-1 text-gray-400 hover:text-gray-600"
                          title="Copy phone number"
                        >
                          {copiedPhoneId === `${applicant.id}-phone1` ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                        <WhatsAppButton countryCode={applicant.countryCode} phone={applicant.phone} />
                      </div>
                      {applicant.phone2 && (
                        <div className="text-sm text-gray-500 flex items-center">
                          {applicant.countryCode2 || '+91'} {applicant.phone2}
                          <button
                            onClick={() => handleCopyPhone(applicant.countryCode2 || '+91', applicant.phone2, `${applicant.id}-phone2`)}
                            className="ml-1 text-gray-400 hover:text-gray-600"
                            title="Copy phone number"
                          >
                            {copiedPhoneId === `${applicant.id}-phone2` ? (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                          </button>
                          <WhatsAppButton countryCode={applicant.countryCode2} phone={applicant.phone2} />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleMarkMsgSent(applicant.id)}
                        disabled={markingSentIds.includes(applicant.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      >
                        {markingSentIds.includes(applicant.id) ? 'Sending...' : 'Msg Sent'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleMarkWaFailed(applicant.id)}
                        disabled={markingFailedIds.includes(applicant.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                      >
                        {markingFailedIds.includes(applicant.id) ? 'Saving...' : "Couldn't Send WA"}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {applicant.position || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {applicant.leadSource || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900">Unable to Send Message</h2>
            <p className="text-sm text-gray-500 mt-1">Leads moved here when WhatsApp outreach could not be sent</p>
          </div>

          {failedApplicants.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <div className="text-gray-500">No failed WhatsApp leads.</div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Retry Success
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead Source
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {failedApplicants.map((applicant) => (
                    <tr key={applicant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/applicants/${applicant.id}`)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline text-left block truncate max-w-[200px]"
                          title={applicant.name}
                        >
                          {applicant.name}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          {applicant.countryCode || '+91'} {applicant.phone}
                          <button
                            onClick={() => handleCopyPhone(applicant.countryCode || '+91', applicant.phone, `${applicant.id}-failed-phone1`)}
                            className="ml-1 text-gray-400 hover:text-gray-600"
                            title="Copy phone number"
                          >
                            {copiedPhoneId === `${applicant.id}-failed-phone1` ? (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                          </button>
                          <WhatsAppButton countryCode={applicant.countryCode} phone={applicant.phone} />
                        </div>
                        {applicant.phone2 && (
                          <div className="text-sm text-gray-500 flex items-center">
                            {applicant.countryCode2 || '+91'} {applicant.phone2}
                            <button
                              onClick={() => handleCopyPhone(applicant.countryCode2 || '+91', applicant.phone2, `${applicant.id}-failed-phone2`)}
                              className="ml-1 text-gray-400 hover:text-gray-600"
                              title="Copy phone number"
                            >
                              {copiedPhoneId === `${applicant.id}-failed-phone2` ? (
                                <CheckIcon className="h-4 w-4 text-green-500" />
                              ) : (
                                <ClipboardDocumentIcon className="h-4 w-4" />
                              )}
                            </button>
                            <WhatsAppButton countryCode={applicant.countryCode2} phone={applicant.phone2} />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleMarkMsgSent(applicant.id)}
                          disabled={markingSentIds.includes(applicant.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          {markingSentIds.includes(applicant.id) ? 'Sending...' : 'Msg Sent'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {applicant.position || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {applicant.leadSource || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NewLeads;
